import { forEach, toLower } from 'lodash-es';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import type { Location } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { CILogger } from 'common/log/log.provider';
import { Loading } from 'common/utils/loading';

import {
    urlQueryParamFlags,
    featureFlags,
    FEATURE_FLAG_URL_PREFIX,
    isUrlQueryParamFlag,
    isFxUrlOverrideEnabled,
    defaultFlags,
    FEATURE_FLAGS_STORAGE_KEY,
} from './featureFlags';
import type { LocalFeatureFlag } from './featureFlags';
import { useFxIntegrationInitialization } from './hooks/useFxIntegrationInitialization';

export function FeatureFlagsSynchronizer({ children }: { children: ReactNode }) {
    const navigate = useNavigate();
    const location = useLocation();
    const isUpdatingURLRef = useRef(false);
    const fxIntegrationState = useFxIntegrationInitialization();

    useEffect(function loadStoredFeatureFlags() {
        try {
            const storedFlags = sessionStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
            if (storedFlags) {
                const parsedFlags = JSON.parse(storedFlags);

                forEach(urlQueryParamFlags, (flag) => {
                    if (parsedFlags[flag] !== undefined) {
                        if (flag === 'fxIntegration' && !isFxUrlOverrideEnabled()) {
                            return;
                        }

                        featureFlags[flag] = parsedFlags[flag];
                    }
                });
            }
        } catch (e) {
            CILogger.getInstance().error('Failed to load feature flags', e);
        }
    }, []);

    useEffect(
        function setupFeatureFlagsHistoryListener() {
            if (!fxIntegrationState.isInitialized) {
                return;
            }

            const synchronizeUrlParamsWithFeatureFlags = (location: Location) => {
                if (isUpdatingURLRef.current) {
                    isUpdatingURLRef.current = false;

                    return;
                }

                const currentParams = new URLSearchParams(location.search);
                const flagsToStore: Partial<Record<LocalFeatureFlag, boolean>> = {};
                const newParams = new URLSearchParams();

                // Copy other parameters
                forEach(Array.from(currentParams.entries()), ([key, value]) => {
                    if (!isUrlQueryParamFlag(key)) {
                        newParams.append(key, value);
                    }
                });

                forEach(urlQueryParamFlags, (flag) => {
                    if (flag === 'fxIntegration' && !isFxUrlOverrideEnabled()) {
                        return;
                    }

                    const flagParamKey = `${FEATURE_FLAG_URL_PREFIX}${flag}`;
                    const urlValue = currentParams.get(flagParamKey) || currentParams.get(toLower(flagParamKey));
                    if (urlValue !== null) {
                        const flagValue = urlValue === 'true';
                        featureFlags[flag] = flagValue;
                        flagsToStore[flag] = flagValue;
                    }

                    // Add to new params only if not default
                    if (featureFlags[flag] !== defaultFlags[flag]) {
                        newParams.append(flagParamKey, String(featureFlags[flag]));
                        flagsToStore[flag] = featureFlags[flag];
                    }
                });

                // Store feature flags
                try {
                    sessionStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(flagsToStore));
                } catch (e) {
                    CILogger.getInstance().error('Failed to store feature flags', e);
                }

                const newParamsString = newParams.toString();
                const currentParamsString = currentParams.toString();

                // Update URL
                if (newParamsString !== currentParamsString) {
                    isUpdatingURLRef.current = true;
                    navigate(
                        { pathname: location.pathname, search: newParamsString, hash: location.hash },
                        { state: location.state, replace: true }
                    );
                }
            };

            synchronizeUrlParamsWithFeatureFlags(location);
        },
        [location, fxIntegrationState.isInitialized]
    );

    return fxIntegrationState.isInitialized ? (
        <>{children}</>
    ) : (
        <LoadingContainer>
            <Loading isLoading={true} />
        </LoadingContainer>
    );
}

const LoadingContainer = styled('div')(() => ({
    position: 'fixed',
    inset: 0,
    display: 'grid',
    placeItems: 'center',
}));
